import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const Login = ({ onToggleMode, showRegisterLink = true }) => {
  const { loginWith2FA, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // First, try 2FA login
      const result = await loginWith2FA(email, password);
      
      if (result.success && result.requires2FA) {
        // 2FA is available, navigate to verification
        navigate('/verificar-2fa', {
          state: {
            email: result.email,
            tempToken: result.tempToken,
            password: result.password
          }
        });
      } else if (!result.success) {
        // Check if error is due to missing SMTP config
        if (result.error && result.error.includes('2FA no está disponible')) {
          // SMTP not configured, try legacy login
          console.log('2FA not available, using legacy login');
          const legacyResult = await login(email, password);
          
          if (!legacyResult.success) {
            setError(legacyResult.error);
          }
          // On success, AuthContext will handle navigation
        } else {
          setError(result.error);
        }
        setLoading(false);
      }
    } catch (err) {
      setError('Error al iniciar sesión. Por favor, intenta nuevamente.');
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md" data-testid="login-card">
      <CardHeader>
        <CardTitle className="text-2xl">Iniciar Sesión</CardTitle>
        <CardDescription>Ingresa tus credenciales para acceder</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive" data-testid="login-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email">Correo Electrónico</Label>
            <Input
              id="email"
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Contraseña</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="text-right">
              <button
                type="button"
                onClick={() => navigate('/recuperar-contrasena')}
                className="text-sm text-purple-600 hover:text-purple-700 hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </div>
          
          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Iniciando sesión...
              </>
            ) : (
              'Iniciar Sesión'
            )}
          </Button>
          
          {showRegisterLink && (
            <div className="text-center text-sm">
              <span className="text-gray-600">¿No tienes cuenta? </span>
              <button
                type="button"
                onClick={onToggleMode}
                className="text-blue-600 hover:text-blue-700 font-medium"
                data-testid="toggle-to-register"
              >
                Regístrate
              </button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default Login;